import { useScaffold } from "@Light/scaffold";
import clsx from "clsx";

export type Query = {
  isUninitialized?: boolean;
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
};

export type QueryLoaderProps = {
  queries?: Query[];
  allowError?: boolean;
  error?: React.ReactNode;
  errorMessage?: string;
  fullHeight?: boolean;
  children?: React.ReactNode;
};

export function chainQueries(queries: Query[]): Query {
  if (queries.length === 0) {
    return { isUninitialized: true };
  }
  if (queries[0].isUninitialized) {
    return { isUninitialized: true };
  }
  for (const query of queries) {
    if (query.isLoading) {
      return {
        isLoading: true,
      };
    }
    if (query.isError) {
      return {
        isError: true,
      };
    }
  }
  if (queries[queries.length - 1].isSuccess) {
    return { isSuccess: true };
  }
  return { isLoading: true };
}

export function useChainQueries(queries: Query[]): Query {
  return chainQueries(queries);
}

export const QueryLoader: React.FC<QueryLoaderProps> = ({
  queries,
  allowError,
  error,
  errorMessage,
  fullHeight,
  children,
}) => {
  const scaffold = useScaffold();
  const query = useChainQueries(queries || []);

  if (queries?.length === 0) {
    return children;
  }

  if (query.isUninitialized || query.isLoading) {
    const Spinner = scaffold.system.spinner;
    return (
      <div
        className={clsx(
          "flex items-center justify-center",
          fullHeight ? "h-full" : "h-40",
        )}
      >
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (query.isError && !allowError) {
    if (error) {
      return error;
    }
    const Field = scaffold.system.field;
    const ErrorMessage = scaffold.system.errorMessage;
    return (
      <Field>
        <ErrorMessage>
          {errorMessage ||
            "There was an unexpected error. Please try again later"}
        </ErrorMessage>
      </Field>
    );
  }

  return children;
};
